/**
 * Dial-code table for the Phone element, shared by the web renderer and
 * fill-by-voice so a spoken number is stored exactly as a typed one:
 * `"+61 412345678"` (dial code, space, national number without its leading 0).
 */

export interface PhoneCountry {
  /** ISO 3166-1 alpha-2 code, uppercase. */
  code: string;
  /** E.164 country calling code with the plus sign, e.g. "+61". */
  dial: string;
  name: string;
}

export const PHONE_COUNTRIES: PhoneCountry[] = [
  { code: "AU", dial: "+61", name: "Australia" },
  { code: "NZ", dial: "+64", name: "New Zealand" },
  { code: "US", dial: "+1", name: "United States" },
  { code: "CA", dial: "+1", name: "Canada" },
  { code: "GB", dial: "+44", name: "United Kingdom" },
  { code: "IE", dial: "+353", name: "Ireland" },
  { code: "IN", dial: "+91", name: "India" },
  { code: "SG", dial: "+65", name: "Singapore" },
  { code: "HK", dial: "+852", name: "Hong Kong" },
  { code: "MY", dial: "+60", name: "Malaysia" },
  { code: "PH", dial: "+63", name: "Philippines" },
  { code: "ID", dial: "+62", name: "Indonesia" },
  { code: "JP", dial: "+81", name: "Japan" },
  { code: "CN", dial: "+86", name: "China" },
  { code: "ZA", dial: "+27", name: "South Africa" },
  { code: "DE", dial: "+49", name: "Germany" },
  { code: "FR", dial: "+33", name: "France" },
  { code: "ES", dial: "+34", name: "Spain" },
  { code: "IT", dial: "+39", name: "Italy" },
  { code: "NL", dial: "+31", name: "Netherlands" },
  { code: "AE", dial: "+971", name: "United Arab Emirates" },
  { code: "BR", dial: "+55", name: "Brazil" },
];

/** The country whose dial code prefixes `value`, longest code winning. */
export const matchPhoneDial = (value: string): PhoneCountry | undefined =>
  PHONE_COUNTRIES.filter((c) => value.startsWith(c.dial)).sort(
    (a, b) => b.dial.length - a.dial.length,
  )[0];

/** Dial code for an alpha-2 country, or undefined when the table has no entry. */
export const dialForCountry = (code: string | undefined): string | undefined =>
  PHONE_COUNTRIES.find((c) => c.code === (code || "").toUpperCase())?.dial;
